/**
 * Prompt sharing — single source of truth
 *
 * Canonical state lives on promptData as:
 * - `permissions` — API-shaped object (companyAccess, teamAccess, arrays, …)
 * - `publishToCommunity` — community flag
 *
 * `visibility` is a UX label set when applying BuildTab presets and on hydrate from API;
 * the slider should reflect permissions via inferPromptVisibility (see BuildTab).
 */

import { inferPromptVisibility } from './inferPromptVisibility';
import { coerceUsageMetadata } from '../constants/usageTargets';
import { DEFAULT_AI_PROVIDER, DEFAULT_INTELLIGENCE_LEVEL } from '../constants/aiRouting';

export { inferPromptVisibility };

const EMPTY_ARRAY_KEYS = [
  'teamAccess',
  'groupAccess',
  'individualAccess',
  'canEdit',
  'canShare',
  'canDelete',
  'viewOnlyAccess'
];

/**
 * Normalize permissions to match backend createPrompt defaults.
 * @param {object|null|undefined} raw
 * @returns {object}
 */
export function normalizePromptPermissions(raw) {
  const defaults = {
    companyAccess: false,
    teamAccess: [],
    groupAccess: [],
    individualAccess: [],
    canEdit: [],
    canShare: [],
    canDelete: [],
    viewOnlyAccess: []
  };
  if (!raw || typeof raw !== 'object') {
    return { ...defaults };
  }
  const p = { ...defaults, ...raw };
  for (const key of EMPTY_ARRAY_KEYS) {
    p[key] = Array.isArray(p[key]) ? [...p[key]] : [];
  }
  p.companyAccess = !!p.companyAccess;
  return p;
}

export function inferModalPermissionLevel(publishToCommunity, permissions) {
  if (publishToCommunity) return 'community';
  const normalized = normalizePromptPermissions(permissions);
  if (normalized.companyAccess) return 'company';
  if (normalized.groupAccess.length > 0) return 'group';
  if (
    normalized.canEdit.length > 0 ||
    normalized.canDelete.length > 0 ||
    normalized.canShare.length > 0 ||
    normalized.viewOnlyAccess.length > 0 ||
    normalized.individualAccess.length > 0
  ) return 'custom';
  if (normalized.teamAccess.length > 0) return 'team';
  return 'private';
}

export function buildPermissionLevelUpdate(currentPrompt, draftLevel, draftPermissions) {
  const publishToCommunity = draftLevel === 'community';
  return {
    permissions: normalizePromptPermissions(draftPermissions),
    ...(publishToCommunity !== !!currentPrompt.publishToCommunity ? { publishToCommunity } : {}),
  };
}

/**
 * Build next permissions + flags for a BuildTab visibility preset.
 * @param {'private'|'team'|'organization'|'community'|'custom'} level
 * @param {{ userTeamId: string|null, creatorId: string|null, prevPermissions: object }} ctx
 * @returns {{ permissions: object, publishToCommunity: boolean, visibility: string }}
 */
export function applyPromptSharingPreset(level, ctx) {
  const { userTeamId, creatorId, prevPermissions } = ctx;
  const prev = normalizePromptPermissions(prevPermissions);
  const emptyArrays = {
    groupAccess: [],
    individualAccess: [],
    canEdit: [],
    canShare: [],
    canDelete: [],
    viewOnlyAccess: []
  };

  if (level === 'private') {
    return {
      permissions: {
        companyAccess: false,
        teamAccess: [],
        ...emptyArrays
      },
      publishToCommunity: false,
      visibility: 'private'
    };
  }
  if (level === 'team') {
    return {
      permissions: {
        companyAccess: false,
        teamAccess: userTeamId ? [userTeamId] : [],
        ...emptyArrays
      },
      publishToCommunity: false,
      visibility: 'team'
    };
  }
  if (level === 'organization') {
    return {
      permissions: {
        companyAccess: true,
        teamAccess: userTeamId ? [userTeamId] : [],
        ...emptyArrays
      },
      publishToCommunity: false,
      visibility: 'organization'
    };
  }
  if (level === 'community') {
    return {
      permissions: {
        companyAccess: true,
        teamAccess: [],
        groupAccess: [],
        individualAccess: [],
        canEdit: [],
        canShare: [],
        canDelete: [],
        viewOnlyAccess: []
      },
      publishToCommunity: true,
      visibility: 'community'
    };
  }
  // custom
  const existingIndividual = prev.individualAccess;
  const hasCreator = creatorId && existingIndividual.includes(creatorId);
  const individualAccess =
    hasCreator || !creatorId ? existingIndividual : [creatorId, ...existingIndividual];
  return {
    permissions: {
      companyAccess: false,
      teamAccess: [],
      groupAccess: [],
      individualAccess,
      canEdit: [...prev.canEdit],
      canShare: [...prev.canShare],
      canDelete: [...prev.canDelete],
      viewOnlyAccess: [...prev.viewOnlyAccess]
    },
    publishToCommunity: false,
    visibility: 'custom'
  };
}

/**
 * Whether the current user may send permission fields on create/update.
 */
export function resolveCanEditPromptPermissions(promptData, user) {
  const isCreator = promptData.userId && user && promptData.userId === user.id;
  let canEditPermissions = isCreator;

  if (!isCreator && promptData.id && promptData.permissions) {
    const perms = promptData.permissions;
    const canDelete = Array.isArray(perms.canDelete) ? perms.canDelete : [];
    if (canDelete.includes(user?.id)) {
      canEditPermissions = true;
    } else if (user?.teamId && canDelete.includes(user.teamId)) {
      canEditPermissions = true;
    }
  }
  return canEditPermissions;
}

/**
 * Build the API payload object for publish (non-draft). Does not perform network calls.
 */
export function buildPromptPublishPayload(promptData, userProfile, user) {
  let permissions = normalizePromptPermissions(promptData.permissions);

  if (promptData.publishToCommunity) {
    permissions = {
      companyAccess: true,
      teamAccess: [],
      groupAccess: [],
      individualAccess: [],
      canEdit: [],
      canShare: [],
      canDelete: [],
      viewOnlyAccess: []
    };
  }

  const canEditPermissions = resolveCanEditPromptPermissions(promptData, user);
  const publishToCommunity = !!promptData.publishToCommunity;

  const usage = coerceUsageMetadata({
    whereToUse: promptData.whereToUse,
    usagePlatform: promptData.usagePlatform,
    usagePlatformOther: promptData.usagePlatformOther,
  });

  const promptToSave = {
    id: promptData.id,
    title: promptData.title,
    description: promptData.description,
    howToUse: promptData.howToUse || '',
    category: promptData.category || null,
    content: promptData.prompt,
    variables: promptData.variables || [],
    tags: promptData.tags || [],
    contextIds: Array.isArray(promptData.contextIds) ? promptData.contextIds : [],
    contextCategories:
      promptData.contextCategories && typeof promptData.contextCategories === 'object'
        ? promptData.contextCategories
        : {},
    contextFolderNames: Array.isArray(promptData.contextFolderNames) ? promptData.contextFolderNames : [],
    aiProvider: promptData.aiProvider || DEFAULT_AI_PROVIDER,
    intelligenceLevel: promptData.intelligenceLevel || DEFAULT_INTELLIGENCE_LEVEL,
    model: promptData.model || null,
    isDraft: false,
    publishedAt: new Date().toISOString(),
    allowCopying: promptData.allowCopying !== undefined ? promptData.allowCopying : true,
    ...(canEditPermissions || !promptData.id ? { publishToCommunity } : {}),
    communityTitle: promptData.communityTitle || '',
    isMultiStep: promptData.isMultiStep || false,
    steps: promptData.steps || [],
    collectionId: publishToCommunity ? null : promptData.collectionId || null,
    collectionName: publishToCommunity ? null : promptData.collectionName || null,
    ...(canEditPermissions || !promptData.id ? { permissions } : {}),
    companyId: userProfile?.companyId || null,
    teamId: userProfile?.teamId || null,
    whereToUse: usage.whereToUse,
    usagePlatform: usage.usagePlatform,
    usagePlatformOther: usage.usagePlatformOther,
    capabilityType: promptData.capabilityType || 'prompt',
  };

  delete promptToSave.contexts;

  return { promptToSave, canEditPermissions };
}
